import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import cn from 'classnames';
import { Field, FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Row } from 'UIPackage/FormModule/components/Row';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  decisionListFindDecision,
  decisionListSetFindData,
} from 'DecisionListPackage/MainModule/actions/decisionListActions';
import { IDecisionListItem } from 'DecisionListPackage/MainModule/interfaces/IDecisionListItem';
import { getDecisionListFindData } from 'DecisionListPackage/MainModule/selectors/decisionListSelectors';
import { IDecisionListFilterParams } from 'DecisionListPackage/MainModule/interfaces/IDecisionListFilterParams';
import { fetchDecisionById } from 'APIPackage/DecisionModule/services/decisionApi';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { REASON_PASSPORT_LINKS_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';

import styles from './ErknmDecisionLinks.module.scss';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { IKnmErknmFormReason } from '../../MainModule/interfaces/IKnmErknmFormReason';
import { getDecisionEvidences } from '../common/getDecisionEvidences';

const ERP_ID_MIN_LENGTH = 12;

interface IDecisionAutocompleteOption {
  label: string;
  value: string;
  decision: IDecisionListItem;
}

const generateDecisionInfoText = (decision: IDecisionListItem) => {
  return `${decision.number ? `${decision.number} ` : ''} ${decision.organizationName ?? ''} ${decision.kno ?? ''}${
    decision.prosecutorOrganization ?? ''
  } ${
    decision.firstDecisionDate
      ? formatDateLocale({
          date: decision.firstDecisionDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
        })
      : ''
  }`;
};

const generateLinkInfoText = (knmLink: IKnmLink) =>
  `${knmLink.erpId ? `${knmLink.erpId} ` : ''}${knmLink.subjectName ? `${knmLink.subjectName} ` : ''}${
    knmLink.inn ? `(ИНН: ${knmLink.inn})` : ''
  }${knmLink.ogrn ? ` (ОГРН: ${knmLink.ogrn})` : ''} ${knmLink.knoOrganizationName ?? ''} ${
    knmLink.prosecutorOfficeName ?? ''
  } ${
    knmLink.startDate
      ? formatDate({
          date: knmLink.startDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
        })
      : ''
  }${
    knmLink.stopDate
      ? ' - '.concat(
          formatDate({
            date: knmLink.stopDate,
            dateFormat: DATE_FORMAT_LONG_FNS,
          }),
        )
      : ''
  }`;

interface IErknmDecisionLinksProps {
  watchOnly: boolean;
  isEnabled: boolean;
  isAddDisabled?: boolean;
  linksName: string;
  reasonsFieldName?: string;
  reason?: IKnmErknmFormReason;
}

interface IErknmDecisionLinksStoreProps {
  formRef: IKnmFormRef | undefined;
  decisions: IDecisionListItem[];
}

const ErknmDecisionLinksWithoutStore = ({
  watchOnly,
  isEnabled,
  isAddDisabled,
  linksName,
  formRef,
  decisions,
  reasonsFieldName,
  reason,
}: IErknmDecisionLinksProps & IErknmDecisionLinksStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const state: IKnmForm | undefined = formRef?.current.getState()?.values;
  const linksValue: IKnmLink[] | undefined = state ? getIn(state, linksName || 'passportLinks') : undefined;
  const link = linksValue?.[0];
  const hasLink = !!link;

  const [decisionsAutocompleteOptions, setDecisionsAutocompleteOptions] = useState<
    IDecisionAutocompleteOption[] | undefined
  >();
  const [erpIdInputValue, setErpIdInputValue] = useState(link ? generateLinkInfoText(link) : '');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [lastDecisionsAutocompleteOptions, setLastDecisionsAutocompleteOptions] = useState<
    IDecisionAutocompleteOption[]
  >([]);
  const [isFounded, setIsFounded] = useState(false);
  const linkValidationFieldName = `${reasonsFieldName}.passportLinkValidation`;

  function findDecisions(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);
      let params: IDecisionListFilterParams = {};

      switch (reason?.type?.name) {
        case 'Незамедлительная контрольная закупка':
          params = {
            searchString: paramErpId,
          };
          break;
        default:
          params = { searchString: paramErpId };
          break;
      }

      dispatch(decisionListFindDecision(params, 2000));
    }
  }

  const debouncedGetDecisions = debounce(findDecisions, 3000);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedGetDecisions(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setDecisionsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetDecisions],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      setErpIdInputValue(value);
      debouncedGetDecisions('');
      setDecisionsAutocompleteOptions(undefined);
      setIsErpIdSelected(true);
    },
    [debouncedGetDecisions],
  );

  useEffect(() => {
    if (isFounded) {
      setDecisionsAutocompleteOptions(
        decisions.map(decision => {
          const decisionText = `${generateDecisionInfoText(decision)}`;

          return {
            value: decisionText,
            label: decisionText,
            decision,
          };
        }),
      );
    }
  }, [decisions, isFounded]);

  useUnmount(() => {
    dispatch(decisionListSetFindData([]));
  });

  useEffect(() => {
    decisionsAutocompleteOptions && setLastDecisionsAutocompleteOptions(decisionsAutocompleteOptions);
  }, [decisionsAutocompleteOptions]);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetDecisions('');
        setDecisionsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetDecisions],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
  }, []);

  const addLink = useCallback(
    (decision: IDecisionListItem) => {
      if (push) {
        validateSelectedLinkDecision(decision).then(valRes => {
          const newLink = {
            erpId: decision.number,
            id: decision.id,
            subjectName: decision.organizationName,
            knoOrganizationName: decision.kno,
            prosecutorOfficeName: decision.prosecutorOrganization,
            startDate: formatDateLocale({ date: decision.firstDecisionDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            linkValidationMessage: typeof valRes === 'string' ? valRes : undefined,
          } as IKnmLink;

          push(linksName || 'passportLinks', newLink);

          if (valRes && typeof valRes === 'object') {
            if (Array.isArray(valRes) && valRes?.length > 0) {
              valRes.forEach(item => {
                push(`${reasonsFieldName}.violationEvidences`, item);
              });
            }
          }
        });
      }
      setIsErpIdSelected(false);
    },
    [push],
  );

  const validateSelectedLinkDecision = useCallback(async (decision: IDecisionListItem) => {
    const decisionId = decision.id;
    const selectedLinkDecisionData: IDecision = await fetchDecisionById(decisionId);

    const evidences = getDecisionEvidences(selectedLinkDecisionData);

    if (evidences && evidences?.length > 0) {
      return evidences;
    }

    return undefined;
  }, []);

  const deleteLink = () => {
    const deletingLinkIndex = 0;

    if (remove) {
      remove(linksName || 'passportLinks', deletingLinkIndex);
      // @ts-ignore
      formRef.current.change(`${reasonsFieldName}.violationEvidences`);
    }
  };

  const handleSaveButtonClick = useCallback(() => {
    const decisionAutocompleteOption = lastDecisionsAutocompleteOptions?.find(
      lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === erpIdInputValue,
    );
    const decision = decisionAutocompleteOption?.decision;

    if (decision) {
      addLink(decision);
    }
  }, [addLink, erpIdInputValue, lastDecisionsAutocompleteOptions]);

  const linkDeleteButtonClickHandler = () => {
    if (!watchOnly && isEnabled) {
      deleteLink();
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    }
  };

  return (
    <>
      <div>
        <div className={styles.DisplayNone}>
          <TextInputField name={linkValidationFieldName} />
          {/* Рендер для регистрации филда в форме */}
          <Field name={linksName}>{() => null}</Field>
        </div>
        <div className={cn('shared-field-row', styles.LinkFields)}>
          <FieldBlock
            id={REASON_PASSPORT_LINKS_BLOCK_ID}
            label={getTitleFieldWithRedStar({
              label: 'Ссылка на паспорт Решения',
              field: linksName,
              isDecision: false,
              isKnm: true,
              isPm: false,
            })}
            placementLabel="left"
            color="black"
          >
            <AutoComplete
              type="area"
              options={decisionsAutocompleteOptions}
              noFoundMessage="Решения, соответствующие указанному учетному номеру, не найдены"
              handleAutoCompleteSelect={handleAutoCompleteSelect}
            >
              <TextInput
                id="linkErpId"
                name="linkErpId"
                placeholder="Введите номер паспорта, выберите из выпадающего списка"
                clearable={isErpIdSelected && !hasLink}
                value={erpIdInputValue}
                onClear={handleTextInputClear}
                onBlur={handleTextInputBlur}
                onChange={handleTextInputChange}
                disabled={watchOnly || isAddDisabled || hasLink}
              />
            </AutoComplete>
          </FieldBlock>
          <Button
            className={styles.SaveButton}
            text="Сохранить"
            color="default"
            size="medium"
            type="button"
            id="saveButton"
            disabled={watchOnly || !isErpIdSelected || hasLink || !isEnabled || isAddDisabled}
            onClick={handleSaveButtonClick}
          />
          <Button
            className={styles.CancelButton}
            variant="link"
            color="primary"
            text="Отмена"
            size="medium"
            type="button"
            disabled={watchOnly || isErpIdSelected || !hasLink || !isEnabled}
            onClick={linkDeleteButtonClickHandler}
          />
        </div>
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            if (!linksName || !props.errors) return null;

            const fieldError = getIn(props.errors, linksName);
            const isFieldTouched = true;

            if (isFieldTouched && fieldError) {
              return (
                <Row>
                  <p className="shared-input-error">{fieldError}</p>
                </Row>
              );
            }

            return null;
          }}
        </FormSpy>
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            if (!linksName || !props.errors) return null;

            const fieldError = getIn(props.errors, linkValidationFieldName);

            const isFieldTouched = true;

            if (isFieldTouched && fieldError) {
              return (
                <Row>
                  <p className="shared-input-error">{fieldError}</p>
                </Row>
              );
            }

            return null;
          }}
        </FormSpy>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmDecisionLinksStoreProps => ({
  formRef: getKnmFormRef(state),
  decisions: getDecisionListFindData(state),
});

export const ErknmDecisionLinks = connect(mapStateToProps)(ErknmDecisionLinksWithoutStore);
